import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Sparkles, CheckCircle2, Loader2, FileText, ChevronRight, Eye, ArrowLeft, Globe, BarChart3, Scale, Shield, Heart, BookOpen, Building, Landmark, TrendingUp, AlertTriangle, Image as ImageIcon, RefreshCw, Info, Plus, Edit, Download, Calendar, Palette, Users, Gavel, Layers, Cpu, Map, Handshake, GitBranch, Receipt, UserSearch, Truck, Banknote, Briefcase, Target, Radar, Flag, ScrollText, BarChart2, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '../contexts/LanguageContext';
import KineticHero from '@/components/KineticHero';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TECH_IMG = 'https://lh3.googleusercontent.com/aida-public/AB6AXuB0sPhxGqDHoMXMKCmEYNPwGXtE8fumfKkDf12sHncwqcYlWHTMvaSTaUKnyBiKEbuIR9GV7KPd5MTYXj8qKAVlD9Xy5eIe3GUd6PpUGTMbPrZrFDhLzMoV-iptV8ZYcvnlqgbPEoqkYdyrfWkop9Z9Cmu1o7zRPHRcLH-69DRamXp16kvgJWkao9ZIf1tIas6k6FeyLSfe6c24Rp-S3cHU-0u6-CGi9AXVdlCLuRO-BwUi9QzyW58L-_MF3fzMX52yPF02zsW7O_bh';

const SECTION_META = {
  country_snapshot: { title: 'Country Snapshot', icon: BarChart3, tier: 'scout', num: 1 },
  executive_summary: { title: 'Executive Summary', icon: Globe, tier: 'scout', num: 2 },
  pestle_analysis: { title: 'PESTLE Analysis', icon: Globe, tier: 'scout', num: 3 },
  market_opportunity: { title: 'Market Opportunity', icon: TrendingUp, tier: 'scout', num: 4 },
  government_capacity: { title: 'Government Capacity', icon: Landmark, tier: 'scout', num: 5 },
  competitive_landscape: { title: 'Competitive Landscape', icon: Building, tier: 'scout', num: 6 },
  business_model: { title: 'Business Model', icon: FileText, tier: 'scout', num: 7 },
  risk_analysis: { title: 'Risk Analysis', icon: AlertTriangle, tier: 'scout', num: 8 },
  financial_projections: { title: 'Financial Projections', icon: Scale, tier: 'scout', num: 9 },
  sovereign_risk: { title: 'Sovereign Risk', icon: Shield, tier: 'scout', num: 10 },
  social_impact: { title: 'Social Impact & ESG', icon: Heart, tier: 'scout', num: 11 },
  governance: { title: 'Governance & PPP', icon: BookOpen, tier: 'scout', num: 12 },
  stakeholder_map: { title: 'Stakeholder Map', icon: Users, tier: 'strategist', num: 13 },
  regulatory_deep_dive: { title: 'Regulatory Deep Dive', icon: Gavel, tier: 'strategist', num: 14 },
  value_chain: { title: 'Value Chain & Margins', icon: Layers, tier: 'strategist', num: 15 },
  tech_readiness: { title: 'Technology Readiness', icon: Cpu, tier: 'strategist', num: 16 },
  entry_strategy: { title: 'Entry Strategy', icon: Map, tier: 'strategist', num: 17 },
  local_partners: { title: 'Local Partner & JV', icon: Handshake, tier: 'strategist', num: 18 },
  scenario_planning: { title: 'Scenario Planning', icon: GitBranch, tier: 'strategist', num: 19 },
  tax_incentives: { title: 'Tax & Incentive Guide', icon: Receipt, tier: 'strategist', num: 20 },
  talent_labor: { title: 'Talent & Labor', icon: UserSearch, tier: 'strategist', num: 21 },
  supply_chain: { title: 'Supply Chain', icon: Truck, tier: 'strategist', num: 22 },
  dfi_landscape: { title: 'DFI & Blended Finance', icon: Banknote, tier: 'sovereign', num: 23 },
  deal_structuring: { title: 'Deal Structuring', icon: Briefcase, tier: 'sovereign', num: 24 },
  ma_targets: { title: 'M&A Targets', icon: Target, tier: 'sovereign', num: 25 },
  geopolitical_brief: { title: 'Geopolitical Brief', icon: Radar, tier: 'sovereign', num: 26 },
  diplomatic_tracker: { title: 'Diplomatic Relations', icon: Flag, tier: 'sovereign', num: 27 },
  investment_memo: { title: 'IC Investment Memo', icon: ScrollText, tier: 'sovereign', num: 28 },
  portfolio_benchmark: { title: 'Portfolio Benchmark', icon: BarChart2, tier: 'sovereign', num: 29 },
  execution_playbook: { title: '100-Day Playbook', icon: Rocket, tier: 'sovereign', num: 30 },
};
const ALL_SECTION_KEYS = Object.keys(SECTION_META).sort((a, b) => SECTION_META[a].num - SECTION_META[b].num);
const TIER_INFO = {
  scout: { name: 'SCOUT', color: '#004ac6', bg: '#e5eeff' },
  strategist: { name: 'STRATEGIST', color: '#00A651', bg: '#ecfdf5' },
  sovereign: { name: 'SOVEREIGN', color: '#7c3aed', bg: '#f3f0ff' },
};

const preprocessMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text;
  const lines = text.split('\n');
  const result = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const next = lines[i + 1] || '';
    result.push(line);
    if (line.trim() && next.trim() && !line.trim().startsWith('|') && !next.trim().startsWith('|') &&
        !line.trim().startsWith('#') && !next.trim().startsWith('#') &&
        !/^[\s]*[-*\d]+[.)]\s/.test(line) && !/^[\s]*[-*\d]+[.)]\s/.test(next) &&
        (line.trim().endsWith('.') || line.trim().endsWith(':')) && next.trim().match(/^[A-Z]/)) {
      result.push('');
    }
  }
  return result.join('\n');
};

function SectionGeneratorPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [countries, setCountries] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState(searchParams.get('country') || '');
  const [country, setCountry] = useState(null);
  const [generatingSections, setGeneratingSections] = useState({});
  const [generatingIllustrations, setGeneratingIllustrations] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    
    axios.get(`${API}/countries`, {  })
      .then(res => setCountries(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedCountryId) {
      
      axios.get(`${API}/countries/${selectedCountryId}`, {  })
        .then(res => setCountry(res.data))
        .catch(() => toast.error('Failed'));
    }
  }, [selectedCountryId]);

  const handleGenerate = async (sectionKey) => {
    if (!selectedCountryId) return;
    try {
      setGeneratingSections(prev => ({ ...prev, [sectionKey]: true }));
      // Start background generation
      await axios.post(`${API}/countries/${selectedCountryId}/generate-section/${sectionKey}?language=${lang || 'en'}`, {}, { timeout: 15000 });
      
      let lastQueueMsg = null;
      // Poll for completion
      const poll = async (attempts = 0) => {
        if (attempts > 90) { // ~7.5 min max
          toast.error(`${SECTION_META[sectionKey]?.title} is taking too long`);
          setGeneratingSections(prev => ({ ...prev, [sectionKey]: false }));
          return;
        }
        try {
          const statusResp = await axios.get(`${API}/countries/${selectedCountryId}/section-status/${sectionKey}`);
          if (statusResp.data.status === 'completed') {
            toast.dismiss(`queue-${sectionKey}`);
            toast.success(`${SECTION_META[sectionKey]?.title} generated!`);
            const updated = await axios.get(`${API}/countries/${selectedCountryId}`);
            setCountry(updated.data);
            setGeneratingSections(prev => ({ ...prev, [sectionKey]: false }));
            return;
          }
          if (statusResp.data.status === 'error') {
            toast.dismiss(`queue-${sectionKey}`);
            toast.error(`Failed: ${statusResp.data.error || 'Generation error'}`);
            setGeneratingSections(prev => ({ ...prev, [sectionKey]: false }));
            return;
          }
          const ahead = statusResp.data.queue_ahead || 0;
          const currentStatus = statusResp.data.status;
          const msg = currentStatus === 'queued'
            ? (ahead > 0 ? `${SECTION_META[sectionKey]?.title}: queued — ${ahead} ahead` : `${SECTION_META[sectionKey]?.title}: starting shortly…`)
            : currentStatus === 'generating'
              ? `${SECTION_META[sectionKey]?.title}: generating with AI engine…`
              : null;
          if (msg && msg !== lastQueueMsg) {
            lastQueueMsg = msg;
            toast.loading(msg, { id: `queue-${sectionKey}`, duration: 60000 });
          }
        } catch {}
        setTimeout(() => poll(attempts + 1), 5000);
      };
      setTimeout(() => poll(0), 3000);
    } catch (error) {
      toast.error(`Failed: ${error.response?.data?.detail || 'Error'}`);
      setGeneratingSections(prev => ({ ...prev, [sectionKey]: false }));
    }
  };

  const handleGenerateIllustration = async (sectionKey) => {
    if (!selectedCountryId) return;
    try {
      setGeneratingIllustrations(prev => ({ ...prev, [sectionKey]: true }));
      const response = await axios.post(`${API}/countries/${selectedCountryId}/generate-illustration/${sectionKey}`, {}, {
        timeout: 120000
      });
      if (response.data.success) {
        toast.success(`${SECTION_META[sectionKey]?.title} illustration created!`);
        const updated = await axios.get(`${API}/countries/${selectedCountryId}`, {});
        setCountry(updated.data);
      }
    } catch (error) {
      toast.error(`Illustration failed: ${error.response?.data?.detail || 'Error'}`);
    } finally {
      setGeneratingIllustrations(prev => ({ ...prev, [sectionKey]: false }));
    }
  };

  const handleGenerateAll = async () => {
    if (!selectedCountryId) { toast.error('Select a country'); return; }
    for (const key of ALL_SECTION_KEYS) {
      await handleGenerate(key);
      // Generate illustration after each section text
      await handleGenerateIllustration(key);
    }
    toast.success('All sections & illustrations generated!');
  };

  const renderSectionCard = (sectionKey) => {
    const meta = SECTION_META[sectionKey];
    if (!meta) return null;
    const Icon = meta.icon;
    const isGenerated = (country?.sections_generated || []).includes(sectionKey);
    const isGenerating = generatingSections[sectionKey];
    const isGeneratingIllustration = generatingIllustrations[sectionKey];
    const content = country?.[sectionKey];
    const illustration = country?.illustrations?.[sectionKey];
    const regenCount = country?.section_regen_counts?.[sectionKey] || 0;

    return (
      <div key={sectionKey} className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid={`gen-section-${sectionKey}`}>
        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#e5eeff] flex items-center justify-center">
                <Icon className="w-4 h-4 text-[#004ac6]" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-[#0b1c30] tracking-tight">{meta.title}</h3>
                {isGenerated && <span className="text-[9px] text-[#434655]">{regenCount}/10 regenerations used</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color: TIER_INFO[meta.tier]?.color, backgroundColor: TIER_INFO[meta.tier]?.bg }}>{meta.num}</span>
              {illustration && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">
                  <ImageIcon className="w-2.5 h-2.5 inline mr-0.5" />Illustrated
                </span>
              )}
              <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                isGenerating ? 'bg-[#e5eeff] text-[#004ac6]' :
                isGenerated ? 'bg-green-50 text-green-600' : 'bg-[#eff4ff] text-[#434655]'
              }`}>{isGenerating ? t('generating') : isGenerated ? t('gen_complete') : t('gen_draft')}</span>
            </div>
          </div>

          {/* Content */}
          {isGenerating ? (
            <div className="flex items-center gap-3 py-6 justify-center">
              <div className="flex items-center gap-2 px-4 py-2 bg-[#eff4ff] rounded-lg">
                <Loader2 className="w-4 h-4 text-[#004ac6] animate-spin" />
                <span className="text-xs text-[#004ac6] font-medium">Generating Synthesis...</span>
              </div>
            </div>
          ) : content ? (
            <div className="mt-2">
              {/* Illustration thumbnail if exists */}
              {illustration && (
                <div className="mb-2 rounded-lg overflow-hidden border border-[#e5eeff]">
                  <img src={illustration} alt={`${meta.title} illustration`} className="w-full h-28 object-cover" />
                </div>
              )}
              {typeof content === 'string' ? (
                <div className="section-content prose prose-sm prose-slate max-w-none prose-headings:text-[#0b1c30] prose-p:text-[#434655] prose-p:leading-relaxed prose-p:text-xs prose-table:border prose-td:border prose-td:px-2 prose-td:py-1 prose-th:border prose-th:px-2 prose-th:py-1 prose-th:bg-[#eff4ff] prose-th:text-[10px] prose-td:text-[10px] max-h-48 overflow-hidden relative">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{preprocessMarkdown(content.substring(0, 600))}</ReactMarkdown>
                  {content.length > 600 && <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent" />}
                </div>
              ) : typeof content === 'object' ? (
                <div className="space-y-2 max-h-48 overflow-hidden relative">
                  {Object.entries(content).slice(0, 3).map(([k, v]) => (
                    <div key={k}>
                      <span className="text-[10px] font-bold text-[#004ac6] uppercase tracking-wider">{k}</span>
                      <p className="text-xs text-[#434655] leading-relaxed">{typeof v === 'string' ? v.substring(0, 150) + '...' : ''}</p>
                    </div>
                  ))}
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent" />
                </div>
              ) : null}

              {/* Metrics for completed sections */}
              {isGenerated && sectionKey === 'financial_projections' && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="bg-[#eff4ff] p-2.5 rounded-lg">
                    <span className="text-[9px] text-[#434655] uppercase tracking-wider">Compliance Score</span>
                    <p className="text-sm font-bold text-[#0b1c30]">98.4%</p>
                  </div>
                  <div className="bg-[#eff4ff] p-2.5 rounded-lg">
                    <span className="text-[9px] text-[#434655] uppercase tracking-wider">Volatility Index</span>
                    <p className="text-sm font-bold text-[#0b1c30]">0.12</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-[#434655] italic py-4 text-center">Not generated yet</p>
          )}

          {/* Illustration generating state */}
          {isGeneratingIllustration && (
            <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg">
              <Loader2 className="w-3.5 h-3.5 text-purple-500 animate-spin" />
              <span className="text-xs text-purple-600 font-medium">Creating illustration...</span>
            </div>
          )}

          {/* Info banner for certain sections */}
          {isGenerated && (sectionKey === 'pestle_analysis' || sectionKey === 'market_opportunity') && (
            <div className="mt-3 flex items-start gap-2 text-[11px] text-[#434655] bg-[#eff4ff] p-2.5 rounded-lg">
              <Info className="w-3.5 h-3.5 text-[#004ac6] mt-0.5 shrink-0" />
              <span>Data sourced from World Bank, IMF & UN Trade reports.</span>
            </div>
          )}

          {/* Actions */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button onClick={() => handleGenerate(sectionKey)} disabled={isGenerating || isGeneratingIllustration}
              className={isGenerated ? "gap-1.5 text-xs text-[#004ac6] border-[#004ac6]/20" : "bg-[#004ac6] text-white gap-1.5 text-xs"}
              variant={isGenerated ? "outline" : "default"} size="sm"
              data-testid={`gen-btn-${sectionKey}`}>
              {isGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : isGenerated ? <RefreshCw className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
              {isGenerated ? t('gen_regenerate') : t('gen_generate')}
            </Button>
            {isGenerated && (
              <Button onClick={() => handleGenerateIllustration(sectionKey)} disabled={isGenerating || isGeneratingIllustration}
                variant="outline" size="sm" className="gap-1.5 text-xs text-purple-600 border-purple-200"
                data-testid={`illust-btn-${sectionKey}`}>
                {isGeneratingIllustration ? <Loader2 className="w-3 h-3 animate-spin" /> : <Palette className="w-3 h-3" />}
                {illustration ? t('gen_regenerate_illustration') : t('gen_generate_illustration')}
              </Button>
            )}
            {isGenerated && (
              <Button onClick={() => navigate(`/platform/countries/${selectedCountryId}/blueprint`)}
                variant="outline" size="sm" className="gap-1.5 text-xs">
                <Eye className="w-3 h-3" /> Preview
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-6 h-6 animate-spin text-[#004ac6]" /></div>;

  return (
    <div data-testid="section-generator-page" className="min-h-screen bg-[#f8f9ff] text-[#0b1c30]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <KineticHero
        uid="sgen"
        palette="violet"
        eyebrow={country ? `${country.name}${country.is_region ? ' · REGIONAL BLOC' : ''} · ${country.sector || 'General'}` : 'Section Generator'}
        title={country ? `${country.name.split(' ')[0]}` : 'Section'}
        accent={country ? 'Blueprint' : 'Generator'}
        subtitle="Synthesizing multi-modal data streams for high-fidelity regional assessment. Review and generate intelligence blocks across Scout, Strategist, and Sovereign tiers."
        right={
          <div className="flex items-center gap-3">
            <Select value={selectedCountryId} onValueChange={setSelectedCountryId}>
              <SelectTrigger data-testid="generator-country-select" className="w-72 h-9 text-xs bg-white">
                <SelectValue placeholder="Select country or regional bloc" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {countries.some((c) => c.is_region) && (
                  <div className="px-2 pt-2 pb-1 text-[9px] font-black uppercase tracking-widest text-[#7c3aed] bg-[#f5f3ff]">
                    Regional Blocs
                  </div>
                )}
                {countries.filter((c) => c.is_region).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    🌐 {c.name}
                    {typeof c.member_count === 'number' ? ` · ${c.member_count} members` : ''}
                    {c.sector ? ` — ${c.sector}` : ''}
                  </SelectItem>
                ))}
                {countries.some((c) => !c.is_region) && (
                  <div className="px-2 pt-2 pb-1 text-[9px] font-black uppercase tracking-widest text-[#004ac6] bg-[#eff4ff]">
                    Individual Countries
                  </div>
                )}
                {countries.filter((c) => !c.is_region).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} — {c.sector || 'General'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleGenerateAll} className="bg-[#004ac6] text-white gap-2 text-xs font-bold h-9" data-testid="generate-all-btn">
              <Sparkles className="w-3.5 h-3.5" /> Generate All
            </Button>
          </div>
        }
      />
      <div className="p-6 lg:p-8">
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-xs text-[#434655]">
          <span>Sovereign IQ · Section Generator</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-[#434655]">
          <span className="text-[10px] uppercase tracking-widest">Regional Intelligence</span>
          <Calendar className="w-3.5 h-3.5" />
          <span>Updated {country ? new Date(country.updated_at).toLocaleDateString() : 'N/A'}</span>
        </div>
      </div>

      {/* Section Cards Grid — grouped by tier */}
      {country ? (
        <div className="space-y-8">
          {['scout', 'strategist', 'sovereign'].map(tier => {
            const tierSections = ALL_SECTION_KEYS.filter(k => SECTION_META[k].tier === tier);
            const info = TIER_INFO[tier];
            return (
              <div key={tier}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[10px] font-black uppercase tracking-[0.15em] px-3 py-1 rounded-full" style={{ color: info.color, backgroundColor: info.bg }}>{info.name}</span>
                  <div className="flex-1 h-px" style={{ backgroundColor: info.bg }} />
                  <span className="text-[10px] text-[#737686]">{tierSections.length} sections</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {tierSections.map(key => renderSectionCard(key))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-16 text-center">
          <Globe className="w-16 h-16 text-[#c3c6d7] mx-auto mb-4" />
          <h2 className="text-lg font-bold text-[#0b1c30] mb-2">Select a Country</h2>
          <p className="text-sm text-[#434655]">Choose a country from the dropdown above to start generating intelligence sections.</p>
        </div>
      )}
      </div>
    </div>
  );
}

export default SectionGeneratorPage;
